import {
  Identity,
  Organization,
  Person,
  Resource,
  ResourceTypes,
  SingleResourceResponse,
  ResourceId,
} from '@vendorflow/types'
import { UserWhoami } from 'features/auth/AzimuthAuth.type'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import { first } from 'lodash'

export function normalizeOrganization(org: Organization): Organization {
  return {
    ...org,
    name: org.name || '',
    mcNumber: org.mcNumber || '',
  }
}

export function normalizeIdentity(identity: Identity): Identity {
  return {
    ...identity,
    firstName: identity.firstName || '',
    lastName: identity.lastName || '',
    displayName: identity.displayName || '',
    department: identity.department || '',
  }
}

export function extractWhoamiResources(response: SingleResourceResponse<Person, ResourceTypes.PEOPLE>): UserWhoami {
  const memberOf = getMemberOfOrganizations(response)
  const result: UserWhoami = {
    person: getPerson(response),
    identity: getAssumesIdentity(response),
    organizations: memberOf.map((it) => normalizeOrganization(convertResourceToEntity(it))),
    administers: [],
  }

  result.administers = getAdministersOrgs(response, result.identity)

  return result
}

function getPerson(response: SingleResourceResponse<Person, ResourceTypes.PEOPLE>): Person {
  return convertResourceToEntity(response.data as Resource<Person, ResourceTypes.PEOPLE>)
}

function getAssumesIdentity(response: SingleResourceResponse<Person, ResourceTypes.PEOPLE>): Identity {
  const assumes: ResourceId<ResourceTypes.IDENTITIES> | undefined = first(
    response.data.relationships!.assumes!.data as ResourceId<ResourceTypes.IDENTITIES>[]
  )

  if (!assumes) {
    throw new Error('"assumes" relationship not found in whoami response')
  }

  const identityResource: Resource<Identity, ResourceTypes.IDENTITIES> | undefined = response.included!.find(
    (it) => it.id === assumes.id && it.type === assumes.type
  )

  if (!identityResource) {
    throw new Error('"assumes" Identity not found in whoami response')
  }

  return normalizeIdentity(convertResourceToEntity(identityResource))
}

function getMemberOfOrganizations(
  response: SingleResourceResponse<Person, ResourceTypes.PEOPLE>
): Resource<Organization, ResourceTypes.ORGANIZATIONS>[] {
  const memberOf: ResourceId<ResourceTypes.ORGANIZATIONS>[] | undefined =
    (response.data.relationships!.memberOf!.data as ResourceId<ResourceTypes.ORGANIZATIONS>[]) || []

  if (!memberOf.length) {
    throw new Error('"memberOf" relationship has zero organizations in whoami response')
  }

  const orgResources: Resource<Organization, ResourceTypes.ORGANIZATIONS>[] = response.included!.filter((it) =>
    memberOf.some((memberOfOrg) => it.id === memberOfOrg.id && it.type === memberOfOrg.type)
  )

  if (!orgResources.length) {
    throw new Error('"memberOf" organizations not found in whoami response')
  }

  return orgResources
}

function getAdministersOrgs(
  response: SingleResourceResponse<Person, ResourceTypes.PEOPLE>,
  assumes: Identity
): ResourceId<ResourceTypes.ORGANIZATIONS>[] {
  const assumesInclude: Resource<Identity, ResourceTypes.IDENTITIES> | undefined = response.included?.find(
    (include) => include.id === assumes.id && include.type === assumes.type
  )

  if (!assumesInclude?.relationships?.administers.data) {
    return []
  }

  return (assumesInclude.relationships.administers.data as ResourceId<ResourceTypes.ORGANIZATIONS>[]).map((it) => ({
    id: it.id,
    type: it.type,
  }))
}
